/**
 * Die Anträge des aufgeklappten Projekts als Unterzeilen der Windpark-Tabelle, gruppiert unter
 * „Genehmigung“. Jede Unterzeile füllt die Spalten des Projekts, jeweils zweizeilig – oben das,
 * worum es geht, darunter die Ergänzung:
 *
 * - Projekt: die Art im Kurznamen, darunter hellgrau die Bezeichnung
 * - Zieltermin: geplante Genehmigung bzw. Bescheid – wie die Inbetriebnahme beim Projekt –,
 *   darunter die Bearbeitung
 * - Status: der Verfahrensstand als dezentere Marke, darunter die Verfahrensfrist (beide
 *   noch Platzhalter, das Backend kennt sie nicht)
 * - Reifegrad: die freigegebenen Nachweise als Balken, darunter „2 von 16“
 * - Wirtschaftlichkeit: die bisher angefallenen Kosten, darunter „von“ den geplanten – welche
 *   Kosten es sind, steht je Wert im Tooltip
 * - Aktionen: Drei-Punkte-Menü mit dem nächsten Schritt im Verfahren (noch Platzhalter) und
 *   „Termin anlegen“ – zum Antrag führt ein Klick auf die Art
 *
 * Zieltermin und Bearbeitung lassen sich mit Recht direkt ändern, wie in den Terminen.
 * Entschiedene Anträge stehen hinten – man sucht sie sonst, und sie zeigen, was erledigt ist.
 */
import React, { useCallback } from "react";
import { Box, Link, Tooltip, Typography } from "@mui/material";
import {
  ArrowForward as StatusChangeIcon,
  AssignmentTurnedInOutlined as PermitsIcon,
  EventOutlined as EventIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router";
import {
  PermitProcedureSummary,
  PermitSummary,
  ProjectSubRow,
} from "../../../../types";
import {
  approvalDate,
  focusOrder,
  procedureHeading,
} from "../../../../utils/projectFocus";
import { referencePath } from "../../../../utils/deadlines";
import { formatCurrency } from "../../../../utils/formatters";
import { colors, fonts } from "../../../../styles/theme";
import InfoTooltip from "../../../common/overlays/InfoTooltip";
import StatusChip from "../../../common/display/StatusChip";
import DetailCell, { detailIconSx } from "../../../common/display/DetailCell";
import ActionsMenu from "../../../common/overlays/ActionsMenu";
import { InlineUserOption } from "../../../common/inputs/InlineUserPicker";
import { EditableDate, EditableUser } from "../sections/InlineDetail";

interface PermitSubRowsOptions {
  /** Die Anträge des aufgeklappten Projekts; ohne gibt es keine Unterzeilen */
  summary: PermitSummary | null;
  /** Ob man Zieltermin, Bearbeitung und Termine am Antrag pflegen darf */
  canEdit: boolean;
  users: InlineUserOption[];
  onPlannedApprovalChange: (procedureId: string, date: string) => void;
  onAssigneeChange: (procedureId: string, userId: string) => void;
  /** Termin am Antrag anlegen – öffnet den Termin-Dialog mit diesem Antrag */
  onAddDeadline: (procedureId: string) => void;
}

const GROUP = {
  key: "genehmigung",
  label: "Genehmigung",
  icon: <PermitsIcon />,
};

/** Zwei Angaben untereinander, als Block in der Mitte der Spalte, linksbündig zueinander */
const Stack: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box
    sx={{
      display: "inline-flex",
      flexDirection: "column",
      alignItems: "flex-start",
      gap: 0.25,
      maxWidth: "100%",
      minWidth: 0,
      textAlign: "left",
    }}
  >
    {children}
  </Box>
);

const secondaryTextSx = {
  display: "block",
  color: colors.text.secondary,
  fontVariantNumeric: "tabular-nums",
} as const;

/**
 * Oben der Balken, darunter mittig „2 von 16“; was gezählt wird, steht im Tooltip. Der Balken hat
 * in jeder Zeile dieselbe feste Breite – so lassen sich die Füllstände untereinander vergleichen,
 * was bei einer Breite nach dem Text darunter nicht ginge.
 */
const RELEASE_BAR_WIDTH = 96;

const ReleaseBar: React.FC<{ procedure: PermitProcedureSummary }> = ({
  procedure,
}) => {
  const share =
    procedure.evidences_total > 0
      ? procedure.evidences_completed / procedure.evidences_total
      : 0;
  return (
    <Tooltip title="Abgeschlossene Nachweise">
      <Box sx={{ display: "inline-grid", gap: 0.5, textAlign: "center" }}>
        <Box
          sx={{
            height: 6,
            width: RELEASE_BAR_WIDTH,
            borderRadius: 3,
            backgroundColor: colors.border.divider,
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              width: `${share * 100}%`,
              height: "100%",
              backgroundColor: colors.status.success.main,
            }}
          />
        </Box>
        <Typography variant="caption" noWrap sx={secondaryTextSx}>
          {procedure.evidences_completed} von {procedure.evidences_total}
        </Typography>
      </Box>
    </Tooltip>
  );
};

// Was die beiden Beträge sind
const ACTUAL_TOOLTIP =
  "Was die Kostenpositionen des Antrags bisher tatsächlich gekostet haben";
const PLANNED_TOOLTIP =
  "Was die Kostenpositionen des Antrags voraussichtlich kosten, meist die Auftragssummen";

const Costs: React.FC<{ procedure: PermitProcedureSummary }> = ({
  procedure,
}) => {
  const { costs_actual: actual, costs_planned: planned } = procedure;
  if (actual === null && planned === null) {
    return (
      <Typography variant="caption" sx={secondaryTextSx}>
        –
      </Typography>
    );
  }
  return (
    // Mittig wie die Scores der Projektzeile darüber
    <Box
      sx={{
        display: "inline-flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 0.25,
      }}
    >
      <InfoTooltip heading="Istkosten" title={ACTUAL_TOOLTIP} placement="top">
        <Typography
          variant="body2"
          noWrap
          sx={{ fontVariantNumeric: "tabular-nums" }}
        >
          {actual !== null ? formatCurrency(actual) : "–"}
        </Typography>
      </InfoTooltip>
      {planned !== null && (
        <InfoTooltip
          heading="Plankosten"
          title={PLANNED_TOOLTIP}
          placement="top"
        >
          <Typography variant="caption" noWrap sx={secondaryTextSx}>
            von {formatCurrency(planned)}
          </Typography>
        </InfoTooltip>
      )}
    </Box>
  );
};

export const usePermitSubRows = ({
  summary,
  canEdit,
  users,
  onPlannedApprovalChange,
  onAssigneeChange,
  onAddDeadline,
}: PermitSubRowsOptions): ((projectId: string) => ProjectSubRow[]) => {
  const navigate = useNavigate();

  return useCallback(
    (projectId: string) =>
      focusOrder(summary?.procedures ?? []).map((procedure) => {
        const { title, subtitle } = procedureHeading(procedure);
        const approval = approvalDate(procedure);
        const procedurePath = referencePath({
          projectId,
          permitProcedureId: procedure.id,
        });

        return {
          id: procedure.id,
          group: GROUP,
          cells: {
            projekt: (
              <Box sx={{ minWidth: 0 }}>
                <Link
                  component="button"
                  type="button"
                  variant="body2"
                  underline="hover"
                  onClick={() => navigate(procedurePath)}
                  sx={{
                    display: "block",
                    maxWidth: "100%",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    fontWeight: fonts.weights.medium,
                    color: colors.text.primary,
                    textAlign: "left",
                  }}
                >
                  {title}
                </Link>
                {subtitle && (
                  <Typography variant="caption" noWrap sx={secondaryTextSx}>
                    {subtitle}
                  </Typography>
                )}
              </Box>
            ),
            targetDate: (
              <Stack>
                <EditableDate
                  value={approval.date}
                  editable={canEdit && approval.plannable}
                  ariaLabel={`${approval.label} ändern: ${title}`}
                  label={approval.label}
                  onChange={(date) =>
                    onPlannedApprovalChange(procedure.id, date)
                  }
                />
                <EditableUser
                  userId={procedure.bearbeitung_user_id}
                  name={procedure.bearbeitung_name}
                  users={users}
                  editable={canEdit}
                  ariaLabel={`Bearbeitung ändern: ${procedure.bearbeitung_name}`}
                  onChange={(userId) => onAssigneeChange(procedure.id, userId)}
                />
              </Stack>
            ),
            gesamtstatus: (
              <Box
                sx={{
                  display: "inline-flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 0.5,
                }}
              >
                {/* Dezenter als der Gesamtstatus darüber: Er ist dessen Unterstatus */}
                <StatusChip
                  label="Verfahrensstand: tbd"
                  color={colors.text.secondary}
                  background={colors.background.tableHeader}
                  variante="outlined"
                />
                <Tooltip title="Verfahrensfrist">
                  <Box>
                    <DetailCell icon={<EventIcon sx={detailIconSx} />}>
                      tbd
                    </DetailCell>
                  </Box>
                </Tooltip>
              </Box>
            ),
            reifegrad: <ReleaseBar procedure={procedure} />,
            wirtschaftlichkeit: <Costs procedure={procedure} />,
          },
          actions: (
            <ActionsMenu
              ariaLabel={`Aktionen zu ${title}`}
              items={[
                {
                  label: "Nächster Schritt (tbd)",
                  icon: <StatusChangeIcon fontSize="small" />,
                  onClick: () => undefined,
                  disabled: true,
                },
                ...(canEdit
                  ? [
                      {
                        label: "Termin anlegen",
                        icon: <EventIcon fontSize="small" />,
                        onClick: () => onAddDeadline(procedure.id),
                        dividerBefore: true,
                      },
                    ]
                  : []),
              ]}
            />
          ),
        };
      }),
    [
      summary,
      canEdit,
      users,
      navigate,
      onPlannedApprovalChange,
      onAssigneeChange,
      onAddDeadline,
    ],
  );
};
